import type {
  AiAnalysisDto,
  AiProviderSettingsDto,
  CameraDto,
  EdgeNodeDto,
  EventDetailDto,
  EventListItemDto,
  EvidenceDto,
  SiteDto,
  UserDto,
  ZoneDto,
} from '@/types/api'
import type { EventStatus, EventTypeName, Severity } from '@/types/eventTypes'

/**
 * Synthetic fixtures for the opt-in mock API. Everything here is fabricated and is only
 * ever reachable when `VITE_USE_MOCK_API=true`, which also pins a permanent amber banner
 * to the top of the app.
 */

/** Deterministic PRNG so a reload does not reshuffle the whole console. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const random = mulberry32(20260824)

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(random() * items.length)] as T
}

function uuid(prefix: string, index: number): string {
  const hex = (index + 1).toString(16).padStart(12, '0')
  return `${prefix}0000-0000-4000-8000-${hex}`
}

function isoAgo(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString()
}

/* ------------------------------------------------------------------- users */

const ALL_PERMISSIONS = [
  'dashboard.read',
  'sites.read',
  'sites.write',
  'cameras.read',
  'cameras.write',
  'zones.read',
  'zones.write',
  'events.read',
  'events.review',
  'settings.manage',
]

/**
 * Sign in as `viewer@…` to exercise the read-only path — no `events.review`, so the
 * review action renders disabled with its explanatory tooltip.
 */
export function mockUserFor(email: string): UserDto {
  const isViewer = email.toLowerCase().startsWith('viewer')
  return {
    id: uuid('u', 1),
    tenantId: uuid('t', 1),
    email,
    displayName: isViewer ? 'Sam Viewer' : 'Alex Operator',
    role: isViewer ? 'Viewer' : 'SiteManager',
    permissions: isViewer
      ? ALL_PERMISSIONS.filter(
          (p) => !p.endsWith('.write') && p !== 'events.review' && p !== 'settings.manage',
        )
      : ALL_PERMISSIONS,
  }
}

/* ------------------------------------------------------------------- sites */

export const sites: SiteDto[] = [
  {
    id: uuid('5', 1),
    name: 'Branch 7 — Riverside',
    code: 'BR7',
    timezone: 'Europe/London',
    orgUnitPath: '/uk/south/branch-7',
    address: '14 Riverside Walk, London',
    cameraCount: 3,
    createdAt: isoAgo(60 * 24 * 120),
  },
  {
    id: uuid('5', 2),
    name: 'Branch 12 — Northgate',
    code: 'BR12',
    timezone: 'Europe/London',
    orgUnitPath: '/uk/north/branch-12',
    address: '2 Northgate Street, Leeds',
    cameraCount: 1,
    createdAt: isoAgo(60 * 24 * 90),
  },
]

/* ----------------------------------------------------------------- cameras */

export const cameras: CameraDto[] = [
  {
    id: uuid('c', 1),
    siteId: sites[0]!.id,
    siteName: sites[0]!.name,
    name: 'Front Counter',
    sourceKind: 'VIDEO_FILE',
    sourceUri: 'file:///samples/videos/front-counter.mp4',
    credentialRef: null,
    edgeNodeId: uuid('e', 1),
    samplingFps: 5,
    loopSource: true,
    enabled: true,
    offlineAfterSeconds: 30,
    status: 'ONLINE',
    lastFrameAt: isoAgo(0.1),
    lastEventAt: isoAgo(4),
    observedFps: 4.9,
    streamLatencyMs: 180,
    zoneCount: 2,
    consecutiveErrors: 0,
    edgeNodeName: 'branch-7-edge',
    credentialStatus: null,
    createdAt: isoAgo(60 * 24 * 100),
  },
  {
    id: uuid('c', 2),
    siteId: sites[0]!.id,
    siteName: sites[0]!.name,
    name: 'Entrance',
    sourceKind: 'RTSP',
    sourceUri: 'rtsp://cam-entrance.branch7.local:554/stream1',
    credentialRef: 'cred_entrance',
    edgeNodeId: uuid('e', 1),
    samplingFps: 5,
    loopSource: false,
    enabled: true,
    offlineAfterSeconds: 30,
    status: 'DEGRADED',
    lastFrameAt: isoAgo(0.4),
    lastEventAt: isoAgo(21),
    observedFps: 2.1,
    streamLatencyMs: 1450,
    zoneCount: 1,
    consecutiveErrors: 2,
    edgeNodeName: 'branch-7-edge',
    // The camera the founder will hit first: a ref with no secret on the edge.
    credentialStatus: 'MISSING',
    createdAt: isoAgo(60 * 24 * 100),
  },
  {
    id: uuid('c', 3),
    siteId: sites[0]!.id,
    siteName: sites[0]!.name,
    name: 'Kitchen Pass',
    sourceKind: 'RTSP',
    sourceUri: 'rtsp://cam-kitchen.branch7.local:554/stream1',
    credentialRef: 'cred_kitchen',
    edgeNodeId: uuid('e', 1),
    samplingFps: 4,
    loopSource: false,
    enabled: true,
    offlineAfterSeconds: 30,
    status: 'OFFLINE',
    lastFrameAt: isoAgo(58),
    lastEventAt: isoAgo(58),
    observedFps: 0,
    streamLatencyMs: null,
    zoneCount: 1,
    consecutiveErrors: 37,
    edgeNodeName: 'branch-7-edge',
    credentialStatus: 'PRESENT',
    createdAt: isoAgo(60 * 24 * 70),
  },
  {
    id: uuid('c', 4),
    siteId: sites[1]!.id,
    siteName: sites[1]!.name,
    name: 'Northgate Entrance',
    sourceKind: 'MOCK',
    sourceUri: 'mock://northgate',
    credentialRef: null,
    edgeNodeId: uuid('e', 2),
    samplingFps: 5,
    loopSource: true,
    enabled: false,
    offlineAfterSeconds: 30,
    status: 'DISABLED',
    lastFrameAt: isoAgo(60 * 30),
    lastEventAt: null,
    observedFps: null,
    streamLatencyMs: null,
    zoneCount: 0,
    consecutiveErrors: 0,
    edgeNodeName: 'branch-12-edge',
    credentialStatus: 'UNKNOWN',
    createdAt: isoAgo(60 * 24 * 30),
  },
]

/* ------------------------------------------------------------------- zones */

export const zones: ZoneDto[] = [
  {
    id: uuid('9', 1),
    cameraId: cameras[0]!.id,
    name: 'Front Counter',
    kind: 'CASHIER',
    polygon: [
      [0.34, 0.42],
      [0.72, 0.4],
      [0.78, 0.86],
      [0.28, 0.9],
    ],
    dwellThresholdSeconds: 180,
    anchor: 'FOOT_POINT',
    anchorOverlapFraction: 0.3,
    unattendedAfterSeconds: null,
    color: '#22a6fa',
    enabled: true,
    configVersion: 17,
    createdAt: isoAgo(60 * 24 * 40),
    updatedAt: isoAgo(60 * 24 * 2),
  },
  {
    id: uuid('9', 2),
    cameraId: cameras[0]!.id,
    name: 'Waiting Area',
    kind: 'WAITING',
    polygon: [
      [0.06, 0.5],
      [0.3, 0.46],
      [0.26, 0.94],
      [0.04, 0.95],
    ],
    dwellThresholdSeconds: 240,
    anchor: 'FOOT_POINT',
    anchorOverlapFraction: 0.3,
    unattendedAfterSeconds: null,
    color: '#f59e0b',
    enabled: true,
    configVersion: 17,
    createdAt: isoAgo(60 * 24 * 40),
    updatedAt: isoAgo(60 * 24 * 9),
  },
  {
    id: uuid('9', 3),
    cameraId: cameras[1]!.id,
    name: 'Doorway',
    kind: 'ENTRANCE',
    polygon: [
      [0.4, 0.3],
      [0.62, 0.3],
      [0.66, 0.92],
      [0.36, 0.92],
    ],
    dwellThresholdSeconds: 60,
    anchor: 'FOOT_POINT',
    anchorOverlapFraction: 0.3,
    unattendedAfterSeconds: null,
    color: '#22c55e',
    enabled: true,
    configVersion: 17,
    createdAt: isoAgo(60 * 24 * 35),
    updatedAt: isoAgo(60 * 24 * 35),
  },
  {
    id: uuid('9', 4),
    cameraId: cameras[2]!.id,
    name: 'Pass Shelf',
    kind: 'PICKUP',
    polygon: [
      [0.2, 0.55],
      [0.8, 0.52],
      [0.82, 0.8],
      [0.18, 0.84],
    ],
    dwellThresholdSeconds: 120,
    anchor: 'FOOT_POINT',
    anchorOverlapFraction: 0.3,
    unattendedAfterSeconds: null,
    color: '#a855f7',
    enabled: false,
    configVersion: 17,
    createdAt: isoAgo(60 * 24 * 20),
    updatedAt: isoAgo(60 * 24 * 20),
  },
]

/* ------------------------------------------------------------------ events */

const IMPLEMENTED: EventTypeName[] = [
  'PERSON_ENTERED_ZONE',
  'PERSON_EXITED_ZONE',
  'PERSON_WAITED_IN_ZONE',
  'CAMERA_OFFLINE',
  'CAMERA_ONLINE',
]

function statusFor(confidence: number): EventStatus {
  if (confidence >= 0.9) return 'AUTO_ACCEPTED'
  if (confidence >= 0.7) return 'NEEDS_REVIEW'
  return 'LOW_CONFIDENCE'
}

function severityFor(eventType: EventTypeName): Severity {
  if (eventType === 'CAMERA_OFFLINE') return 'HIGH'
  if (eventType === 'PERSON_WAITED_IN_ZONE') return 'LOW'
  return 'INFO'
}

export interface MockEvent extends EventDetailDto {
  /** Kept alongside so the list projection is trivially derivable. */
  _minutesAgo: number
}

function buildEvent(index: number): MockEvent {
  const minutesAgo = Math.round(index * 17 + random() * 9)
  const camera = pick(cameras.filter((c) => c.status !== 'DISABLED'))
  const cameraZones = zones.filter((zone) => zone.cameraId === camera.id)
  const eventType = pick(IMPLEMENTED)
  const isCameraEvent = eventType === 'CAMERA_OFFLINE' || eventType === 'CAMERA_ONLINE'
  const zone = isCameraEvent ? undefined : cameraZones[0]
  const confidence = isCameraEvent ? 1 : Number((0.6 + random() * 0.4).toFixed(2))
  const pointInTime = eventType !== 'PERSON_WAITED_IN_ZONE' && eventType !== 'CAMERA_OFFLINE'
  const durationSeconds = pointInTime ? 0 : Math.round(45 + random() * 400)
  const startedAt = isoAgo(minutesAgo)
  const endedAt = pointInTime
    ? startedAt
    : new Date(Date.parse(startedAt) + durationSeconds * 1000).toISOString()
  const eventId = uuid('7', index)
  const trackRef = `track_${12000 + index * 7}`

  const evidence: EvidenceDto[] = isCameraEvent
    ? []
    : [
        {
          evidenceId: `${eventId}-thumb`,
          kind: 'THUMBNAIL',
          objectKey: `t/tenant/c/${camera.id}/2026/08/24/${eventId}-thumb.jpg`,
          contentType: 'image/svg+xml',
          sizeBytes: 24_000,
          width: 320,
          height: 180,
          capturedAt: startedAt,
        },
        {
          evidenceId: `${eventId}-snap`,
          kind: 'SNAPSHOT',
          objectKey: `t/tenant/c/${camera.id}/2026/08/24/${eventId}-snap.jpg`,
          contentType: 'image/svg+xml',
          sizeBytes: 180_000,
          width: 1280,
          height: 720,
          capturedAt: startedAt,
        },
      ]

  return {
    _minutesAgo: minutesAgo,
    id: eventId,
    clientEventId: uuid('b', index),
    siteId: camera.siteId,
    siteName: camera.siteName ?? null,
    cameraId: camera.id,
    cameraName: camera.name,
    zoneId: zone?.id ?? null,
    zoneName: zone?.name ?? null,
    edgeNodeId: camera.edgeNodeId ?? null,
    edgeNodeName: camera.edgeNodeName,
    eventType,
    subjectType: isCameraEvent ? 'CAMERA' : 'ANONYMOUS_PERSON',
    subjectRef: isCameraEvent ? camera.id : trackRef,
    startedAt,
    endedAt,
    durationSeconds,
    confidence,
    status: statusFor(confidence),
    severity: severityFor(eventType),
    attributes: isCameraEvent
      ? { offlineAfterSeconds: camera.offlineAfterSeconds }
      : { dwellThresholdSeconds: zone?.dwellThresholdSeconds ?? 180, frameCount: 120 + index },
    source: 'EDGE_AI',
    modelVersion: 'yolov8n@1.0.0',
    trackerVersion: 'bytetrack@1.0.0',
    ruleVersion: 'zone-presence@1.0.0',
    edgeVersion: '0.1.0',
    configVersion: 17,
    schemaVersion: 1,
    receivedAt: new Date(Date.parse(endedAt) + 1400).toISOString(),
    thumbnailEvidenceId: evidence[0]?.evidenceId ?? null,
    observation: isCameraEvent
      ? null
      : {
          observationId: uuid('a', index),
          cameraId: camera.id,
          edgeNodeId: camera.edgeNodeId ?? '',
          trackRef,
          observedFrom: startedAt,
          observedTo: endedAt,
          frameCount: 120 + index,
          meanConfidence: confidence,
          minConfidence: Number((confidence - 0.2).toFixed(2)),
          detectorClass: 'person',
          geometry: {
            zoneId: zone?.id ?? null,
            footPointSamples: [
              [0.41, 0.88],
              [0.42, 0.88],
              [0.44, 0.87],
            ],
            bboxFirst: [0.36, 0.55, 0.47, 0.9],
            bboxLast: [0.38, 0.57, 0.49, 0.92],
          },
          modelVersion: 'yolov8n@1.0.0',
          trackerVersion: 'bytetrack@1.0.0',
        },
    evidence,
    reviews: [],
  }
}

export const events: MockEvent[] = Array.from({ length: 84 }, (_, index) => buildEvent(index)).sort(
  (a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt),
)

export function toListItem(event: MockEvent): EventListItemDto {
  return {
    id: event.id,
    siteId: event.siteId,
    siteName: event.siteName,
    cameraId: event.cameraId,
    cameraName: event.cameraName,
    zoneId: event.zoneId,
    zoneName: event.zoneName,
    eventType: event.eventType,
    subjectType: event.subjectType,
    subjectRef: event.subjectRef,
    startedAt: event.startedAt,
    endedAt: event.endedAt,
    durationSeconds: event.durationSeconds,
    confidence: event.confidence,
    status: event.status,
    severity: event.severity,
    thumbnailEvidenceId: event.thumbnailEvidenceId ?? null,
    profile: profileOf(event.id),
  }
}

/** The latest AI reading of an event, shaped as the API's EventProfileDto. */
function profileOf(eventId: string): EventListItemDto['profile'] {
  const latest = aiAnalyses.find((analysis) => analysis.eventId === eventId)
  if (!latest) return null
  return {
    activityLabel: latest.activityLabel,
    apparentAgeGroup: latest.apparentAgeGroup ?? null,
    apparentGender: latest.apparentGender ?? null,
    observed: latest.observed,
    createdAt: latest.createdAt,
  }
}

/* -------------------------------------------------------------- edge nodes */

/**
 * The platform's current config version. Bumped by every zone write, exactly like the
 * real API, so the Edge Nodes screen can show a node that has not caught up yet.
 */
export const platform = { configVersion: 17 }

export const edgeNodes: EdgeNodeDto[] = [
  {
    id: uuid('e', 1),
    name: 'branch-7-edge',
    siteId: sites[0]!.id,
    siteName: sites[0]!.name,
    status: 'ONLINE',
    hostname: 'edge01',
    agentVersion: '0.1.0',
    lastHeartbeatAt: isoAgo(0.1),
    configVersion: 17,
    currentConfigVersion: 17,
    outboxDepth: 4,
    deadLetterDepth: 0,
    cameraCount: 3,
    lastEventAt: isoAgo(4),
    processingFps: 3.5,
    heartbeatIntervalSeconds: 5,
    createdAt: isoAgo(60 * 24 * 100),
  },
  {
    /*
     * Deliberately unhealthy: offline, behind on config, and holding a backlog. This is
     * the node that makes the Edge Nodes screen worth looking at, and the one that makes
     * `409 EDGE_NODE_OFFLINE` reachable from the camera test.
     */
    id: uuid('e', 2),
    name: 'branch-12-edge',
    siteId: sites[1]!.id,
    siteName: sites[1]!.name,
    status: 'OFFLINE',
    hostname: 'edge-northgate',
    agentVersion: '0.0.9',
    lastHeartbeatAt: isoAgo(47),
    configVersion: 15,
    currentConfigVersion: 17,
    outboxDepth: 128,
    deadLetterDepth: 2,
    cameraCount: 1,
    lastEventAt: isoAgo(60 * 26),
    processingFps: null,
    heartbeatIntervalSeconds: 15,
    createdAt: isoAgo(60 * 24 * 40),
  },
]

/* ------------------------------------------------------------- ai provider */

/**
 * Mutable so `PUT /settings/ai-provider` can be exercised end to end. There is no key
 * here, and there never will be: the mock stores only whether one was set, and a mask —
 * exactly what the real API returns.
 */
export const aiProviderSettings: AiProviderSettingsDto & { _hasKey: boolean } = {
  provider: 'OPENAI',
  baseUrl: 'https://api.openai.com/v1',
  visionModel: 'gpt-5.6-terra',
  managementModel: 'gpt-5.6-terra',
  enabled: true,
  apiKeyConfigured: true,
  dailyCallLimit: 200,
  autoVerifyEnabled: false,
  personAttributesEnabled: true,
  expressionReadingEnabled: false,
  callsUsedToday: 0,
  limitReachedAt: null,
  apiKeyMask: 'sk-••••••••A91F',
  source: 'DATABASE',
  updatedAt: isoAgo(60 * 24 * 3),
  updatedByUserId: uuid('u', 1),
  _hasKey: true,
}

/* ------------------------------------------------------------ ai analyses */

/** Populated by `POST /events/{id}/ai-analysis`; newest first when listed. */
export const aiAnalyses: AiAnalysisDto[] = []

/**
 * A synthetic interpretation. Written to obey the same product guardrails the real
 * prompt does: it describes movement and timing only — never an emotion, a mood, a
 * demographic or an identity — and it hedges where four frames genuinely cannot settle
 * the question.
 */
export function buildAiAnalysis(
  eventId: string,
  evidenceIds: string[],
  question: string | null,
): AiAnalysisDto {
  const event = events.find((candidate) => candidate.id === eventId)
  const zoneName = event?.zoneName ?? 'the monitored zone'
  const trackRef = event?.subjectRef ?? 'track_unknown'
  const duration = event?.durationSeconds ?? 0

  return {
    id: `ai-${Math.random().toString(16).slice(2, 10)}`,
    eventId,
    provider: aiProviderSettings.provider,
    model: aiProviderSettings.visionModel ?? 'gpt-5.6-terra',
    promptVersion: 'event-analysis@5.0.0',
    observed: `A person (${trackRef}) is present in ${zoneName} across the reviewed frames and remains within its boundary throughout. No second tracked person enters the frame in the sampled interval.`,
    // Measured from the event, never from the mocked "model". A mock that invents a
    // number the API computes is how the panel came to render a field the server had
    // no column for -- the failure the DTO parity gate exists to catch.
    presenceSeconds: duration > 0 ? duration : null,
    activitySeconds: evidenceIds.length >= 2 && duration > 0 ? Math.round(duration * 0.9) : null,
    activityFrameCount: evidenceIds.length >= 2 ? evidenceIds.length : null,
    sampledFrameCount: evidenceIds.length,
    confidenceLabel: evidenceIds.length >= 3 ? 'MEDIUM' : 'LOW',
    operationalInterpretation:
      duration > 180
        ? 'Possible service response delay: the dwell exceeds the configured threshold for this zone and no service interaction is visible in the sampled frames.'
        : 'Routine zone presence. Nothing in the sampled frames indicates an exception.',
    caveats: [
      `Only ${evidenceIds.length} frame(s) were reviewed; anything between them is unobserved.`,
      'No employee identity is available — people are per-camera tracking handles only.',
      'Frame sampling means a brief interaction could fall entirely between two frames.',
      ...(question ? ['The answer is scoped to the question asked, not to the whole event.'] : []),
    ],
    evidenceIds,
    latencyMs: 2800 + Math.round(random() * 900),
    tokenUsage: { input: 1840, output: 210 },
    question,
    activityLabel: 'HAND_TO_HEAD',
    personStillPresent: null,
    // Closed-set fields only, and only when the tenant allows them (ADR-0011). The
    // prose above stays free of them, as the real prompt's does.
    apparentAgeGroup: aiProviderSettings.personAttributesEnabled === false ? null : 'ADULT',
    apparentGender:
      aiProviderSettings.personAttributesEnabled === false ? null : 'NOT_DETERMINABLE',
    createdByUserId: uuid('u', 1),
    createdAt: new Date().toISOString(),
  }
}

/* ---------------------------------------------------------------- preview */

/**
 * Detection boxes for the preview overlay, normalised `[x1, y1, x2, y2]`.
 *
 * They are placed relative to each camera's zones so the overlay actually demonstrates
 * the thing it exists for: a person read *against* a zone boundary. `tick` shifts them
 * slightly so a refresh visibly produces a new frame rather than an identical one.
 */
export function previewDetectionsFor(
  cameraId: string,
  tick: number,
): Array<{ trackRef: string; bbox: [number, number, number, number]; confidence: number; class: string }> {
  const drift = ((tick % 5) - 2) * 0.015
  const cameraZones = zones.filter((zone) => zone.cameraId === cameraId)
  if (cameraZones.length === 0) return []

  return cameraZones.slice(0, 2).map((zone, index) => {
    const centroid = zone.polygon.reduce(
      (accumulator, point) => [accumulator[0] + point[0], accumulator[1] + point[1]] as [number, number],
      [0, 0] as [number, number],
    )
    const cx = centroid[0] / zone.polygon.length + drift
    const cy = centroid[1] / zone.polygon.length
    const halfWidth = 0.055
    const height = 0.3

    return {
      trackRef: `track_${(12000 + index * 7 + tick).toString(36)}_${index + 1}`,
      bbox: [
        Math.max(0, cx - halfWidth),
        Math.max(0, cy - height * 0.62),
        Math.min(1, cx + halfWidth),
        Math.min(1, cy + height * 0.38),
      ] as [number, number, number, number],
      confidence: Number((0.78 + ((tick + index) % 4) * 0.05).toFixed(2)),
      class: 'person',
    }
  })
}

/* ----------------------------------------------------------------- imagery */

/**
 * A synthetic "camera frame" as an SVG with explicit pixel dimensions, so the zone
 * editor's `naturalWidth`/`naturalHeight` maths has something real (and deliberately
 * non-16:9) to letterbox.
 */
export function frameSvg(label: string, width = 1280, height = 960): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#12203f"/>
      <stop offset="100%" stop-color="#060d20"/>
    </linearGradient>
    <pattern id="grid" width="80" height="80" patternUnits="userSpaceOnUse">
      <path d="M 80 0 L 0 0 0 80" fill="none" stroke="#1b3160" stroke-width="1"/>
    </pattern>
  </defs>
  <rect width="${width}" height="${height}" fill="url(#g)"/>
  <rect width="${width}" height="${height}" fill="url(#grid)"/>
  <rect x="0" y="${height - 140}" width="${width}" height="140" fill="#0a1733" opacity="0.8"/>
  <text x="40" y="80" fill="#e6ecf7" font-family="monospace" font-size="34">${label}</text>
  <text x="40" y="128" fill="#7c90b4" font-family="monospace" font-size="22">SYNTHETIC FRAME — ${width}×${height}</text>
  <text x="40" y="${height - 84}" fill="#7c90b4" font-family="monospace" font-size="20">MOCK API · not a real camera</text>
  <circle cx="${width * 0.45}" cy="${height * 0.62}" r="46" fill="none" stroke="#22a6fa" stroke-width="3"/>
  <rect x="${width * 0.4}" y="${height * 0.45}" width="120" height="240" fill="none" stroke="#22a6fa" stroke-width="2" stroke-dasharray="8 6"/>
</svg>`
}
